import { useCallback, useRef, useState } from "react";
import { useAudioPreferences } from "@/hooks/useAudioPreferences";
import { useAudioControls } from "@/hooks/useAudioControls";

/**
 * Public entry point for the ambient soundscape feature. Composes two
 * focused hooks -- useAudioPreferences (sessionStorage persistence) and
 * useAudioControls (audio-element playback) -- so each concern stays
 * independently readable/testable, while this hook's own external
 * shape (the object it returns) is unchanged for existing consumers
 * (Navbar.js, MobileMenu.js).
 */
export const useSoundscape = () => {
  const audioRef = useRef(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.2);
  const [expanded, setExpanded] = useState(false);

  useAudioPreferences({ hasEntered, playing, muted, volume, setHasEntered, setPlaying, setMuted, setVolume });
  const { enterWithSound, togglePlay } = useAudioControls({ audioRef, playing, setPlaying, muted, volume, setHasEntered });

  const toggleMute = useCallback(() => setMuted((m) => !m), []);
  const toggleExpanded = useCallback(() => setExpanded((e) => !e), []);

  return {
    audioRef,
    hasEntered,
    playing,
    muted,
    volume,
    expanded,
    setVolume,
    enterWithSound,
    togglePlay,
    toggleMute,
    toggleExpanded,
  };
};
